import React, {
  useEffect, useMemo, useRef, useState,
} from "react";

import { Button } from "antd";
import moment from "moment";
import { toast } from "react-toastify";

import StyledScrollbar from "../../../../Common/UIComponents/StyledScrollbar";
import SuccessAnimation from "../../../../Common/UIComponents/SuccessAnimation";
import { formatAddressLine } from "../../Supply/DeliveryDetailDrawer";
import CreateStepLayout from "./CreateStepLayout";

import { convertUtcToLocal } from "../../../../Common/utils/dateUtils";
import {
  buildPickupRequestPayload,
  formatPickupDateDisplay,
  getBoxesSummary,
  getSubmissionId,
  isFedexPickupType,
  isLocalCourierType,
  PICKUP_TYPE_KEYS,
} from "../utils";

import { createPickupRequest } from "../../../../../actions/supplyPickup";

import "./SummaryStep.css";

const formatSubmittedOn = (submitDate) => {
  if (!submitDate) return "—";
  return moment(new Date(convertUtcToLocal(submitDate))).format("DD MMM YYYY");
};

const formatSampleCount = (count) => {
  const n = Number(count);
  if (!Number.isFinite(n) || n < 0) return "—";
  return String(n).padStart(2, "0");
};

export default function SummaryStep({
  pickupTypeLabel = "FedEx Pickup",
  pickupTypeKey = "fedex-pickup",
  schedule = null,
  packaging = null,
  shippingMethod = null,
  trackingIds = [],
  selectedSubmissions = [],
  onBack,
  onSubmitSuccess,
}) {
  const isFedexPickup = isFedexPickupType(pickupTypeKey);
  const isFedexDropoff = pickupTypeKey === PICKUP_TYPE_KEYS.FEDEX_DROPOFF;
  const isLocalCourier = isLocalCourierType(pickupTypeKey);
  const boxes = packaging?.boxes || [];
  const [submitting, setSubmitting] = useState(false);
  const [showSuccessAnimation, setShowSuccessAnimation] = useState(false);
  const confirmationRef = useRef(null);

  const pickupAddress = schedule?.pickupAddress || {};

  const pickupDateLabel = formatPickupDateDisplay(schedule?.pickupDate, "dddd, MMMM D, YYYY");
  const earliest = schedule?.earliestPossibleTime || "—";
  const latest = schedule?.latestPossibleTime || "—";
  const timeSlot = `${earliest} – ${latest}`;
  const boxesSummary = useMemo(() => getBoxesSummary(boxes), [boxes]);
  const serviceLabel = schedule?.fedexService || "FedEx Overnight";
  const addressLine = formatAddressLine(pickupAddress, "—");

  useEffect(() => {
    if (!showSuccessAnimation) return undefined;

    const timer = setTimeout(() => {
      setShowSuccessAnimation(false);
      onSubmitSuccess(confirmationRef.current || {});
    }, 2000);

    return () => clearTimeout(timer);
    // Advance once after successful submit animation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSuccessAnimation]);

  const leftDetailRows = [
    ...(!isFedexDropoff ? [{
      key: "expected",
      label: "Expected pickup",
      value: (
        <span className="SummaryStep__ExpectedPickup">
          <span className="SummaryStep__ExpectedDate">{pickupDateLabel}</span>
          <span className="SummaryStep__ExpectedTime">{timeSlot}</span>
        </span>
      ),
    }] : []),
    { key: "category", label: "Pickup Category", value: pickupTypeLabel },
    ...(isFedexPickup ? [{ key: "service", label: "Service", value: serviceLabel }] : []),
    ...(!isLocalCourier ? [{ key: "boxes", label: "Boxes", value: boxesSummary }] : []),
  ];

  const locationValue = (
    <div className="SummaryStep__LocationBlock">
      <div className="SummaryStep__LocationRow">
        <span className="SummaryStep__LocationKey">Contact</span>
        <span className="SummaryStep__LocationVal">{pickupAddress.name || "—"}</span>
      </div>
      <div className="SummaryStep__LocationRow">
        <span className="SummaryStep__LocationKey">Phone</span>
        <span className="SummaryStep__LocationVal">{pickupAddress.phone || "—"}</span>
      </div>
      <div className="SummaryStep__LocationRow">
        <span className="SummaryStep__LocationKey">Email</span>
        <span className="SummaryStep__LocationVal">{pickupAddress.email || "—"}</span>
      </div>
      <div className="SummaryStep__LocationRow">
        <span className="SummaryStep__LocationKey">Address</span>
        <span className="SummaryStep__LocationVal">{addressLine}</span>
      </div>
    </div>
  );

  const rightDetailRows = [
    { key: "location", label: isFedexDropoff ? "Address Details" : "Pickup Location", value: locationValue },
  ];

  const renderDetailColumn = (rows) => (
    <div className="SummaryStep__DetailList">
      {rows.map((row, index) => (
        <React.Fragment key={row.key}>
          {index > 0 && (
            <div className="SummaryStep__DetailDivider" role="presentation" />
          )}
          <div className="SummaryStep__DetailRow">
            <span className="SummaryStep__DetailLabel">{row.label}</span>
            <div className="SummaryStep__DetailValue">{row.value}</div>
          </div>
        </React.Fragment>
      ))}
    </div>
  );

  const handleSubmit = async () => {
    if (submitting) return;
    setSubmitting(true);

    const payload = buildPickupRequestPayload({
      pickupTypeKey,
      shippingMethod,
      schedule,
      packaging,
      trackingIds,
      selectedSubmissions,
    });

    const {
      success,
      message,
      fedex_confirmation_number: confirmationNumber,
      fedex_labels: fedexLabels,
    } = await createPickupRequest(payload);

    setSubmitting(false);

    if (!success) {
      toast.error(message || "Failed to create pickup request.");
      return;
    }

    const labels = Array.isArray(fedexLabels) ? fedexLabels : [];

    // Billable stamp / local courier have no labels, so fall back to the IDs the user entered.
    let nextTrackingId = labels.find((label) => label?.tracking_number)?.tracking_number || null;
    if (!nextTrackingId && trackingIds?.[0]?.trackingId) {
      nextTrackingId = trackingIds[0].trackingId;
    }

    confirmationRef.current = {
      confirmationNumber: confirmationNumber || null,
      labels,
      trackingId: nextTrackingId,
    };
    setShowSuccessAnimation(true);
  };

  return (
    <CreateStepLayout
      pickupTypeLabel={pickupTypeLabel}
      pickupTypeKey={pickupTypeKey}
      activeStepKey="summary"
      onBack={onBack}
    >
      <div className="SummaryStep">
        <div className="SummaryStep__ScrollArea">
          <StyledScrollbar noScrollX>
            <div className="SummaryStep__Inner">
              <div className="SummaryStep__Header">
                <h1 className="SummaryStep__Title">Summary</h1>
                <p className="SummaryStep__Subtitle">
                  Review pickup details before submitting
                </p>
              </div>

              <div className="SummaryStep__Card SummaryStep__Card--full">
                <h2 className="SummaryStep__SectionLabel">Pickup Details</h2>
                <div className="SummaryStep__ReviewGrid">
                  {renderDetailColumn(leftDetailRows)}
                  {renderDetailColumn(rightDetailRows)}
                </div>
              </div>

              <div className="SummaryStep__Card SummaryStep__Card--full SummaryStep__SubmissionsCard">
                <h2 className="SummaryStep__SectionLabel">
                  Selected Submissions
                  {selectedSubmissions.length > 0 ? ` (${selectedSubmissions.length})` : ""}
                </h2>
                {selectedSubmissions.length === 0 ? (
                  <p className="SummaryStep__EmptySubmissions">No submissions selected.</p>
                ) : (
                  <div className="SummaryStep__SubmissionsList">
                    <table className="SummaryStep__SubmissionsTable">
                      <thead>
                        <tr>
                          <th>Submission Name</th>
                          <th>Submitted On</th>
                          <th>Submitted By</th>
                          <th className="SummaryStep__NumCol">Samples</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedSubmissions.map((row) => {
                          const id = getSubmissionId(row);
                          return (
                            <tr key={id || row.submit_name}>
                              <td>
                                <div className="SummaryStep__SubmissionName">
                                  {row.submit_name || "—"}
                                </div>
                                <div className="SummaryStep__SubmissionMeta">
                                  {id || row.po || "—"}
                                </div>
                              </td>
                              <td>{formatSubmittedOn(row.submit_date)}</td>
                              <td>{row.submitted_by || "—"}</td>
                              <td className="SummaryStep__NumCol">
                                {formatSampleCount(row.num_samples)}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </StyledScrollbar>
        </div>

        <div className="SummaryStep__Footer SummaryStep__Footer--split">
          <Button
            type="default"
            className="SummaryStep__GoBackBtn"
            onClick={onBack}
            disabled={submitting || showSuccessAnimation}
          >
            Go Back
          </Button>
          <Button
            type="primary"
            className="SummaryStep__SubmitBtn"
            onClick={handleSubmit}
            loading={submitting}
            disabled={submitting || showSuccessAnimation}
          >
            Submit
          </Button>
        </div>
      </div>

      {showSuccessAnimation && (
        <SuccessAnimation message="Pickup placed!" />
      )}
    </CreateStepLayout>
  );
}
